/**
 * Dashboard Page
 *
 * Comprehensive dashboard for SimCheck.ai showing:
 * - World map with color-coded test execution status
 * - Real-time execution feed
 * - Key metrics (modems, success rate, etc.)
 * - Queue and schedule monitor
 * - eSIM inventory widget
 * - Global location summary
 */

import React, { useState, useEffect } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { MetricCard } from '@/components/dashboard/MetricCard';
import { Card, Spinner } from '@/components/ui';
import { Smartphone, PlayCircle, TrendingUp, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { SelectOrganizationButton } from '@/components/admin/SelectOrganizationButton';
import { LineChart } from '@/components/charts/LineChart';
import { PieChart } from '@/components/charts/PieChart';
import {
  TimeRangeSelector,
  TimeRange,
  getDefaultTimeRange,
  getTimeRangeHours,
} from '@/components/charts/TimeRangeSelector';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useRunningExecutions } from '@/hooks/useRealtimeExecutions';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserTimezone } from '@/hooks/useDateFormat';

// Dashboard components
import WorldMap from '@/components/dashboard/WorldMap';
import ExecutionFeed from '@/components/dashboard/ExecutionFeed';
import QueueMonitor from '@/components/dashboard/QueueMonitor';
import ESimInventoryWidget from '@/components/dashboard/ESimInventoryWidget';
import LocationSummary from '@/components/dashboard/LocationSummary';
import LocationDetailPanel from '@/components/dashboard/LocationDetailPanel';
import { ExecutionQuotaWidget } from '@/components/dashboard/ExecutionQuotaWidget';

export function Dashboard() {
  const isMobile = useIsMobile();
  const tz = useUserTimezone();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(getDefaultTimeRange());

  // Calculate hours from time range for data fetching
  const hoursRange = getTimeRangeHours(timeRange);

  // Fetch real dashboard data with 30-second auto-refresh
  const {
    metrics,
    executionTrends,
    performanceBreakdown,
    loading,
    error,
    refresh,
    isRefreshing,
  } = useDashboardMetrics(hoursRange, 60000);

  // Real-time running executions count
  const { runningCount } = useRunningExecutions();

  // Handle location click from map
  const handleLocationClick = (locationId: string) => {
    setSelectedLocationId(locationId);
  };

  // Format execution trends for chart
  const trendsData = executionTrends.map((item) => ({
    time: item.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: tz }),
    success: item.successful,
    failed: item.failed,
  }));

  // Format performance data for pie chart (using hex colors for Recharts SVG)
  const performanceData = performanceBreakdown
    ? [
        { name: 'Successful', value: performanceBreakdown.successful, color: '#22c55e' },
        { name: 'Failed', value: performanceBreakdown.failed, color: '#ef4444' },
        { name: 'Pending', value: performanceBreakdown.pending, color: '#f97316' },
      ]
    : [];

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Dashboard
            </h1>
            <p className="text-body text-muted-foreground">
              Monitor your telecom testing operations in real-time
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={isRefreshing}
              aria-label="Refresh"
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          error === 'No organization selected for viewing' ? (
            <div className="flex items-center justify-between gap-4 p-4 bg-status-warning-bg border border-status-warning/20 rounded-lg text-status-warning">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>No organization selected for viewing</span>
              </div>
              <SelectOrganizationButton
                className="flex items-center gap-2 shrink-0 border-status-warning/30 text-status-warning hover:bg-status-warning/10"
                superAdminLabel="View dashboard as:"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-status-error-bg border border-status-error/20 rounded-lg text-status-error">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )
        )}

        {/* World Map - Hero Component */}
        <WorldMap
          height={isMobile ? '240px' : '400px'}
          onLocationClick={handleLocationClick}
          refreshInterval={30000}
        />

        {/* Hero Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {loading ? (
            // Loading skeletons
            <>
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="p-4 animate-pulse">
                  <div className="h-8 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-6 bg-muted rounded w-3/4" />
                </Card>
              ))}
            </>
          ) : (
            <>
              <MetricCard
                icon={<Smartphone className="w-8 h-8" />}
                label="Modems Online"
                value={metrics?.activeModems ?? 0}
                subValue={`of ${metrics?.totalModems ?? 0} total`}
                color="var(--accent-cyan)"
              />
              <MetricCard
                icon={<PlayCircle className="w-8 h-8" />}
                label="Active Executions"
                value={runningCount}
                trend={runningCount > 0 ? { value: runningCount, direction: 'up' as const } : undefined}
                color="var(--accent-green)"
              />
              <MetricCard
                icon={<TrendingUp className="w-8 h-8" />}
                label="Success Rate (24h)"
                value={`${Number(metrics?.successRate ?? 0).toFixed(1)}%`}
                trend={
                  metrics?.successRate
                    ? {
                        value: metrics.successRate >= 95 ? 0 : parseFloat((100 - metrics.successRate).toFixed(1)),
                        direction: metrics.successRate >= 95 ? 'up' : 'down',
                      }
                    : undefined
                }
                color="var(--accent-magenta)"
              />
              <MetricCard
                icon={<Clock className="w-8 h-8" />}
                label="Avg Duration"
                value={metrics?.avgDuration ? `${metrics.avgDuration}s` : '--'}
                subValue={`${metrics?.todayExecutions ?? 0} tests today`}
                color="var(--accent-purple)"
              />
            </>
          )}
        </div>

        {/* Charts and Execution Feed Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Execution Trends Chart */}
          <Card padding="lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-h3 font-semibold text-foreground">
                  Execution Trends ({timeRange.label})
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Success vs failure rates over time
                </p>
              </div>
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
            {loading ? (
              <div className="h-[250px] flex items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : trendsData.length > 0 ? (
              <LineChart
                data={trendsData}
                lines={[
                  { dataKey: 'success', name: 'Successful', color: '#22c55e' },
                  { dataKey: 'failed', name: 'Failed', color: '#ef4444' },
                ]}
                xAxisKey="time"
                height={250}
                showDots={false}
                curveType="monotone"
              />
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No execution data available
              </div>
            )}
          </Card>

          {/* Real-Time Execution Feed */}
          <ExecutionFeed limit={8} />
        </div>

        {/* Queue, eSIM and Execution Quota Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <QueueMonitor />
          <ESimInventoryWidget />
          <ExecutionQuotaWidget />
        </div>

        {/* Performance Breakdown and Location Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card padding="lg">
            <h3 className="text-h3 font-semibold text-foreground mb-4">
              Performance Breakdown
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Total executions by status
            </p>
            {loading ? (
              <div className="h-[250px] flex items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : performanceData.some((d) => d.value > 0) ? (
              <PieChart
                data={performanceData}
                height={250}
                donut
                centerValue={performanceBreakdown ? performanceBreakdown.successful + performanceBreakdown.failed + performanceBreakdown.pending : 0}
                centerLabel="Total Tests"
              />
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No execution data available
              </div>
            )}
          </Card>

          <LocationSummary timeRange={timeRange} />
        </div>
      </div>

      {/* Location Detail Panel */}
      <LocationDetailPanel
        locationId={selectedLocationId}
        onClose={() => setSelectedLocationId(null)}
      />

    </AppShell>
  );
}
